import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef } from '@angular/material/snack-bar';

export interface ToastOptions {
  message: string;
  duration?: number;
  action?: string;
  panelClass?: string | string[];
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private currentSnackBar: MatSnackBarRef<any> | null = null;

  private defaultConfig: MatSnackBarConfig = {
    duration: 3600,
    horizontalPosition: 'center',
    verticalPosition: 'top'
  };

  constructor(private snackBar: MatSnackBar) {}

  /**
   * Show a success toast message
   */
  showSuccess(message: string, options: Partial<ToastOptions> = {}) {
    return this.show(message, {
      ...options,
      panelClass: ['app-snackbar', 'app-snackbar--success']
    });
  }

  /**
   * Show an error toast message
   */
  showError(message: string, options: Partial<ToastOptions> = {}) {
    return this.show(message, {
      ...options,
      duration: options.duration ?? 4500,
      action: options.action ?? 'OK',
      panelClass: ['app-snackbar', 'app-snackbar--error']
    });
  }

  /**
   * Show an info toast message
   */
  showInfo(message: string, options: Partial<ToastOptions> = {}) {
    return this.show(message, {
      ...options,
      panelClass: ['app-snackbar', 'app-snackbar--info']
    });
  }

  /**
   * Show a warning toast message
   */
  showWarning(message: string, options: Partial<ToastOptions> = {}) {
    return this.show(message, {
      ...options,
      duration: options.duration ?? 4200,
      action: options.action ?? 'OK',
      panelClass: ['app-snackbar', 'app-snackbar--warning']
    });
  }

  /**
   * Show a loading toast message (persistent until dismissed)
   */
  showLoading(message: string, options: Partial<ToastOptions> = {}) {
    return this.show(message, {
      ...options,
      duration: 0, // Persistent
      panelClass: ['app-snackbar', 'app-snackbar--loading']
    });
  }

  /**
   * Show a custom toast with full control
   */
  showCustom(message: string, panelClass: string | string[], options: Partial<ToastOptions> = {}) {
    return this.show(message, {
      ...options,
      panelClass
    });
  }

  /**
   * Show a toast with copy to clipboard functionality
   */
  showCopyable(message: string, textToCopy: string, options: Partial<ToastOptions> = {}) {
    const snackBarRef = this.show(message, {
      ...options,
      action: 'Copy',
      panelClass: ['app-snackbar', 'app-snackbar--info']
    });

    snackBarRef.onAction().subscribe(() => {
      navigator.clipboard.writeText(textToCopy).then(() => {
        this.showSuccess('Copied to clipboard!');
      }).catch(() => {
        this.showError('Failed to copy to clipboard');
      });
    });

    return snackBarRef;
  }

  /**
   * Show an action toast with custom action
   */
  showAction(message: string, action: string, actionCallback?: () => void, options: Partial<ToastOptions> = {}) {
    const snackBarRef = this.show(message, {
      ...options,
      action
    });

    if (actionCallback) {
      snackBarRef.onAction().subscribe(actionCallback);
    }

    return snackBarRef;
  }

  /**
   * Dismiss all active snackbars
   */
  dismissAll() {
    this.snackBar.dismiss();
  }

  /**
   * Internal method to create and present snackbar
   */
  private show(message: string, options: Partial<ToastOptions> = {}) {
    if (this.currentSnackBar) {
      this.currentSnackBar.dismiss();
    }

    const config: MatSnackBarConfig = {
      ...this.defaultConfig,
      ...options,
      duration: options.duration ?? this.defaultConfig.duration
    };

    this.currentSnackBar = this.snackBar.open(message, options.action || undefined, config);

    this.currentSnackBar.afterDismissed().subscribe(() => {
      this.currentSnackBar = null;
    });

    return this.currentSnackBar;
  }

  /**
   * Utility methods for common scenarios
   */

  showNetworkError() {
    return this.showError('You appear to be offline. Check your connection and try again.', {
      duration: 5000
    });
  }

  showUnauthorized() {
    return this.showError('You do not have permission to do that.', {
      duration: 4000
    });
  }

  showServerError() {
    return this.showError('Something went wrong on our side. Please try again in a moment.', {
      duration: 4000
    });
  }

   showValidationError(message: string = 'Please review the highlighted information and try again.') {
     return this.showWarning(message, {
       duration: 4000
     });
   }

  showSaved() {
    return this.showSuccess('Changes saved.', {
      duration: 2400
    });
  }

  showDeleted() {
    return this.showSuccess('Item deleted.', {
      duration: 2400
    });
  }

  showLocationPermissionDenied() {
    return this.showWarning('Location access is off. Enable it in your device settings to continue.', {
      duration: 5000
    });
  }

  showBiometricError() {
    return this.showError('Biometric sign-in failed. Try again or use your password.', {
      duration: 4000
    });
  }

  showComingSoon() {
    return this.showInfo('This feature is coming soon.', {
      duration: 3000
    });
  }
}
